// src/pages/FOInventoryPage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

// Reusing existing components for consistency
import MyItemsTable from "../../components/operationsAssociate/MyItemsTable";
import ReportUsageModal from "../../components/operationsAssociate/ReportUsageModal";
import FOSidebar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import { Navigate } from "react-router-dom";

const API_URL = "https://ims.holdthechild.net";

const FOInventoryPage = () => {
    // State to hold the issued items for the logged-in user
    const [myItems, setMyItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // State to manage the usage reporting modal
    const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Function to handle user logout and redirect
    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/";
    };

    // Function to fetch the issued items for the current user
    const fetchMyItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            // This endpoint fetches items issued to the logged-in user
            const response = await axios.get(`${API_URL}/api/issued-items/my-items`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMyItems(response.data.data || []);
            setLoading(false);
        } catch (err) {
            console.error("Failed to fetch issued items:", err);
            setError("Failed to fetch your issued items. Please try again later.");
            setLoading(false);
            toast.error("Failed to load your inventory. 😢");
        }
    };

    // Fetch items on component mount
    useEffect(() => {
        fetchMyItems();
    }, []);

    // Handlers for modal and table actions
    const handleConfirmReceipt = async (itemId) => {
        const token = localStorage.getItem('token');
        try {
            await axios.post(`${API_URL}/api/issued-items/confirm-receipt/${itemId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Receipt confirmed!");
            fetchMyItems(); // Refresh the list after the action
        } catch (error) {
            toast.error("Failed to confirm receipt.");
            console.error(error);
        }
    };

    const handleOpenUsageModal = (item) => {
        setSelectedItem(item);
        setIsUsageModalOpen(true);
    };

    const handleReturnItem = async (itemId) => {
        const token = localStorage.getItem('token');
        try {
            await axios.post(`${API_URL}/api/issued-items/return/${itemId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Item returned to inventory.");
            fetchMyItems(); // Refresh the list
        } catch (error) {
            toast.error("Failed to return item.");
            console.error(error);
        }
    };

    const handleUsageModalClose = () => {
        setIsUsageModalOpen(false);
        setSelectedItem(null);
        fetchMyItems(); // Refresh the list after an action
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans">
            {/* The Operations Associate sidebar is now a shared component */}
            <FOSidebar
                visible={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                onLogout={handleLogout}
            />
            <div className="flex flex-col flex-1 overflow-hidden">
                {/* The top navigation bar is also a shared component */}
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />
                <main className="flex-1 overflow-auto p-6 pt-24 md:pt-20 md:ml-64">
                    <div className="flex justify-between items-center mb-6 mt-20">
                        <h1 className="text-3xl font-bold text-gray-800">
                            My Issued Inventory
                        </h1>
                    </div>

                    {loading && (
                        <div className="p-8 text-center text-gray-500">Loading your inventory...</div>
                    )}

                    {!loading && error && (
                        <div className="p-8 text-center text-red-500 font-semibold">{error}</div>
                    )}

                    {!loading && !error && (
                        <MyItemsTable
                            items={myItems}
                            onConfirmReceipt={handleConfirmReceipt}
                            onReportUsage={handleOpenUsageModal}
                            onReturnItem={handleReturnItem}
                        />
                    )}

                    {/* The modal for reporting usage, only rendered when needed */}
                    {isUsageModalOpen && selectedItem && (
                        <ReportUsageModal
                            closeModal={handleUsageModalClose}
                            item={selectedItem}
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

export default FOInventoryPage;
